import {
  FC,
  PropsWithChildren,
  useState,
  createContext,
  useContext,
  useCallback,
} from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { OrderTracking, Shipment } from '../../../__generated__/loma';

export type OrderTrackingQueryData = { orderTracking: OrderTracking };

export interface OrderTrackingProviderValue {
  orderTrackingData?: OrderTracking;
}

export interface OrderTrackingProviderProps {
  value: OrderTrackingProviderValue;
}

export interface OrderTrackingValue {
  orderTrackingData?: OrderTracking;
  getCurrentShipment: () => Shipment | null;
  setCurrentShipment: (id: string) => void;
  mainShipmentId?: string | null;
}

export const OrderTrackingContext = createContext<OrderTrackingValue>({
  orderTrackingData: undefined,
  getCurrentShipment: () => null,
  setCurrentShipment: () => {},
  mainShipmentId: null,
});

export const OrderTrackingProvider: FC<
  PropsWithChildren<OrderTrackingProviderProps>
> = ({ value: { orderTrackingData }, children }) => {
  const [mainShipmentId, setMainShipmentId] = useState<string | null>(
    orderTrackingData?.orders[0].mainShipmentId ?? null,
  );
  const [updatingMainShipment, setUpdatingMainShipment] = useState(false);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const currentSearchParams = new URLSearchParams(searchParams?.toString());

  const getCurrentShipment = useCallback(() => {
    const { shipments } = orderTrackingData?.orders[0] || {};

    if (!mainShipmentId || !shipments) return null;
    const shipment = shipments.find(
      ({ shipmentId }) => shipmentId === mainShipmentId,
    );

    return shipment || null;
  }, [orderTrackingData, updatingMainShipment, mainShipmentId]);

  const setCurrentShipment = useCallback(
    (newMainShipmentId: string) => {
      currentSearchParams.set('tracking_numbers', newMainShipmentId);
      const updatedUrl = `${pathname}?${currentSearchParams.toString()}`;
      router.replace(updatedUrl);

      setMainShipmentId(newMainShipmentId);
    },
    [setUpdatingMainShipment],
  );

  return (
    <OrderTrackingContext.Provider
      value={{
        orderTrackingData,
        getCurrentShipment,
        setCurrentShipment,
        mainShipmentId: orderTrackingData?.orders[0].mainShipmentId,
      }}
    >
      {children}
    </OrderTrackingContext.Provider>
  );
};

export default () => useContext(OrderTrackingContext);
